/**
 * FilePond Upload Component
 *
 * A plug-and-play file upload component that works anywhere.
 * Just drop it in and it handles everything: upload, validation, error handling.
 *
 * @example
 * ```tsx
 * <FilePondUpload
 *   onUploadComplete={(fileId, fileUrl, filename) => {
 *     // Do something with the uploaded file
 *   }}
 * />
 * ```
 */

import type { AxiosError } from 'axios'
import type { RefObject } from 'react'

import FilePondPluginFileValidateSize from 'filepond-plugin-file-validate-size'
import FilePondPluginFileValidateType from 'filepond-plugin-file-validate-type'
import FilePondPluginImagePreview from 'filepond-plugin-image-preview'
import { Loader2 } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { FilePond, registerPlugin } from 'react-filepond'

import type { FileResponseDTO } from '@/utils/api/file-service/models'

import { useDeleteFile, useOpenUploadFile, useUploadFile } from '@/utils/api/file-service'

// Import styles
import 'filepond/dist/filepond.min.css'
import 'filepond-plugin-image-preview/dist/filepond-plugin-image-preview.css'

// Register plugins once
registerPlugin(
  FilePondPluginImagePreview,
  FilePondPluginFileValidateType,
  FilePondPluginFileValidateSize,
)

const DEFAULT_ACCEPT_TYPES = ['image/png', 'image/jpeg', 'image/jpg', 'application/pdf']
const IMAGE_ACCEPT_TYPES = ['image/png', 'image/jpeg', 'image/jpg', 'image/gif', 'image/webp']
const DOCUMENT_ACCEPT_TYPES = ['image/png', 'image/jpeg', 'image/jpg', 'application/pdf']

export interface FilePondUploadProps {
  /** Called when file upload completes successfully */
  onUploadComplete?: (fileId: string, fileUrl: string, filename: string, fileSize: number) => void

  /** Called when upload fails */
  onUploadError?: (error: string) => void

  /** Called when a file is removed */
  onRemove?: (fileId: string) => void

  /** Called when upload state changes */
  onUploadStateChange?: (isUploading: boolean) => void

  /** Accepted file types - defaults to images and PDFs */
  accept?: string[]

  /** Max file size (e.g., '5MB', '10MB') - defaults to '10MB' */
  maxFileSize?: string

  /** Allow multiple files - defaults to false */
  multiple?: boolean

  /** Max number of files when multiple is true - defaults to 5 */
  maxFiles?: number

  /** Disabled state */
  disabled?: boolean

  /** Custom label - supports HTML */
  label?: string

  /** Additional CSS classes */
  className?: string

  /** Optional external ref for triggering FilePond actions */
  pondRef?: RefObject<FilePond | null>

  /** Show required indicator */
  required?: boolean

  /** User authentication status */
  isAuthenticated?: boolean

  /** Username or email for unauthenticated uploads */
  usernameOrEmail?: string
  /** Called when a file is added to FilePond (client-side) */
  onAddFile?: (file: File) => void
}

export function FilePondUpload({
  onUploadComplete,
  onUploadError,
  onRemove,
  onAddFile,
  onUploadStateChange,
  accept = DEFAULT_ACCEPT_TYPES,
  maxFileSize = '10MB',
  multiple = false,
  maxFiles = 5,
  disabled = false,
  label,
  className = '',
  pondRef,
  required = false,
  isAuthenticated = true,
  usernameOrEmail = '',
}: FilePondUploadProps) {
  const internalPondRef = useRef<FilePond>(null)
  const activePondRef = pondRef ?? internalPondRef
  const uploadMutation = useUploadFile()
  const openUploadMutation = useOpenUploadFile()
  const deleteMutation = useDeleteFile()
  const [isUploading, setIsUploading] = useState(false)

  // Store file IDs for removal
  const fileIdsRef = useRef<Map<string, string>>(new Map())

  // Notify parent when upload state changes
  useEffect(() => {
    onUploadStateChange?.(isUploading)
  }, [isUploading, onUploadStateChange])

  // Generate default label if not provided
  const defaultLabel = `
    <div style="display: flex; flex-direction: column; align-items: center; gap: 0.5rem;">
      <span style="font-weight: 500;">
        Drag & Drop your ${multiple ? 'files' : 'file'} or 
        <span class="filepond--label-action">Browse</span>
        ${required ? '<span style="color: #ef4444; margin-left: 0.25rem;">*</span>' : ''}
      </span>
      <span style="font-size: 0.875rem; opacity: 0.7;">
        ${accept.includes('application/pdf') ? 'PDF, ' : ''}${accept.some(t => t.startsWith('image')) ? 'Images' : 'Files'} 
        (max ${maxFileSize})
      </span>
    </div>
  `

  return (
    <div className={`relative ${className}`}>
      <FilePond
        ref={activePondRef}
        allowMultiple={multiple}
        maxFiles={multiple ? maxFiles : 1}
        maxFileSize={maxFileSize}
        acceptedFileTypes={accept}
        disabled={disabled}
        // instantUpload
        labelIdle={label || defaultLabel}
        name="file"
        credits={false}

        // Server configuration
        server={{
          process: async (
            _fieldName,
            file,
            _metadata,
            load,
            error,
            _progress,
            abort,
          ) => {
            setIsUploading(true)
            try {
              const response = isAuthenticated
                ? await uploadMutation.mutateAsync({ data: { file } })
                : await openUploadMutation.mutateAsync({ data: { file, usernameOrEmail } })

              const fileData = response.data as FileResponseDTO
              const wrappedData = (response.data as { data?: FileResponseDTO }).data
              const serverId = fileData.fileId ?? wrappedData?.fileId ?? ''
              const fileUrl = fileData.path ?? wrappedData?.path ?? ''
              const originalFilename = fileData.originalFilename ?? wrappedData?.originalFilename ?? (file as File).name
              const fileSize = fileData.size ?? wrappedData?.size ?? 0

              if (!serverId) {
                const errorMessage = 'Upload succeeded but did not return a file id.'
                console.error('FilePond: Upload error:', errorMessage)
                error(errorMessage)
                onUploadError?.(errorMessage)
              }
              else {
                // Store the server file ID mapped to FilePond's unique file ID
                fileIdsRef.current.set(file.name, serverId)

                // Notify success
                load(serverId)

                // Call the callback
                onUploadComplete?.(
                  serverId,
                  fileUrl,
                  originalFilename,
                  fileSize,
                )
              }
            }
            catch (err) {
              const axiosError = err as AxiosError<{ message?: string }>
              const errorMessage = axiosError.response?.data?.message
                || axiosError.message
                || 'Upload failed. Please try again.'

              console.error('FilePond: Upload error:', errorMessage)
              error(errorMessage)
              onUploadError?.(errorMessage)
            }
            finally {
              setIsUploading(false)
            }

            return {
              abort: () => {
                setIsUploading(false)
                abort()
              },
            }
          },

          revert: async (uniqueFileId, load, errorCallback) => {
            const serverId = (uniqueFileId as string) || ''
            try {
              await deleteMutation.mutateAsync({ fileId: serverId })
              onRemove?.(serverId)
              const entry = Array.from(fileIdsRef.current.entries()).find(
                ([_, id]) => id === serverId,
              )
              if (entry) {
                fileIdsRef.current.delete(entry[0])
              }
              load()
            }
            catch (err) {
              const axiosError = err as AxiosError<{ message?: string }>
              const errorMessage
                = axiosError.response?.data?.message
                  || axiosError.message
                  || 'Failed to delete file. Please try again.'

              if (typeof errorCallback === 'function') {
                errorCallback(errorMessage)
              }
              onUploadError?.(errorMessage)
            }
          },
        }}

        // Event handlers
        onprocessfilestart={() => setIsUploading(true)}
        onprocessfile={(error) => {
          setIsUploading(false)
          if (error) {
            console.error('FilePond processing error:', error)
          }
        }}
        onprocessfileabort={() => setIsUploading(false)}
        onprocessfilerevert={() => setIsUploading(false)}
        onaddfile={(_, fileItem) => {
          try {
            const fpFile = (fileItem && (fileItem as any).file) as File | undefined
            if (fpFile && typeof onAddFile === 'function')
              onAddFile(fpFile)
          }
          catch {
            // ignore
          }
        }}
        onremovefile={(error) => {
          if (error) {
            console.error('FilePond removal error:', error)
          }
        }}

        // Styling
        stylePanelLayout="compact"
        styleButtonRemoveItemPosition="right"
        styleButtonProcessItemPosition="right"
        styleLoadIndicatorPosition="right"
        styleProgressIndicatorPosition="right"
      />
      {isUploading && (
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center rounded-lg bg-background/60">
          <div className="flex items-center gap-2 rounded-full bg-card/90 px-3 py-2 text-sm font-medium text-muted-foreground shadow">
            <Loader2 className="size-4 animate-spin text-primary" />
            <span>Uploading…</span>
          </div>
        </div>
      )}
    </div>
  )
}

/**
 * Simple wrapper for single file upload that auto-clears on success
 * Perfect for form fields where you only want one file at a time
 */
export function FilePondSingleUpload(props: Omit<FilePondUploadProps, 'multiple' | 'maxFiles'>) {
  return <FilePondUpload {...props} multiple={false} maxFiles={1} />
}

/**
 * Wrapper for image-only uploads
 */
export function FilePondImageUpload(props: Omit<FilePondUploadProps, 'accept'>) {
  return (
    <FilePondUpload
      {...props}
      accept={IMAGE_ACCEPT_TYPES}
    />
  )
}

/**
 * Wrapper for document uploads (PDFs and images)
 */
export function FilePondDocumentUpload(props: Omit<FilePondUploadProps, 'accept'>) {
  return (
    <FilePondUpload
      {...props}
      accept={DOCUMENT_ACCEPT_TYPES}
    />
  )
}
